/**
 * MM Bundle Builder
 * A portable, theme-agnostic bundle builder for Shopify.
 * Uses Alpine.js for reactivity with URL-based state management.
 */

(function () {
  "use strict";

  // ========================================
  // Global Initialization Tracker
  // ========================================

  // Use window-level tracking to persist across script reloads (e.g., from Barba.js page transitions)
  window._cBundleState = window._cBundleState || {
    initializedSections: new Set(),
    componentInstances: new Map(),
    registered: false,
  };

  const initializedSections = window._cBundleState.initializedSections;
  const componentInstances = window._cBundleState.componentInstances;

  // ========================================
  // Pure Business Logic Functions (Testable)
  // ========================================

  /**
   * Calculate total quantity from items map
   * @param {Object} items - Map of variantId -> quantity
   * @returns {number} Total quantity
   */
  function calculateTotalQuantity(items) {
    return Object.values(items).reduce((sum, qty) => sum + qty, 0);
  }

  /**
   * Get effective minimum (max of URL original_min and schema min)
   * @param {number|null} originalMin - From URL params
   * @param {number} schemaMin - From section schema
   * @returns {number} Effective minimum
   */
  function getEffectiveMin(originalMin, schemaMin) {
    return Math.max(originalMin || 0, schemaMin || 1);
  }

  /**
   * Validate if quantity is within bounds
   * @param {number} total - Current total quantity
   * @param {number} effectiveMin - Effective minimum
   * @param {number} max - Maximum allowed
   * @returns {boolean} Whether quantity is valid
   */
  function validateQuantity(total, effectiveMin, max, selectedPack) {
    return total >= selectedPack && total >= effectiveMin && total <= max;
  }

  /**
   * Get active tier based on effective minimum
   * @param {number} effectiveMin - Effective minimum quantity
   * @param {Array} tiers - Array of tier configurations
   * @returns {Object|null} Active tier or null
   */
  function getActiveTier(effectiveMin, tiers) {
    if (!tiers || tiers.length === 0) return null;

    return (
      tiers
        .slice()
        .sort((a, b) => b.min_quantity - a.min_quantity)
        .find((tier) => effectiveMin >= tier.min_quantity) || tiers[0]
    );
  }

  /**
   * Get unlocked gifts based on total quantity
   * @param {number} totalQty - Current total quantity
   * @param {Array} giftConfigs - Array of gift configurations
   * @returns {Array} Array of unlocked gifts
   */
  function getUnlockedGifts(totalQty, giftConfigs) {
    if (!giftConfigs || giftConfigs.length === 0) return [];

    return giftConfigs.filter((gift) => {
      if (gift.always_included) return true;
      return totalQty >= gift.unlock_quantity;
    });
  }

  /**
   * Calculate combined savings percentage from all sources
   * Uses multiplicative stacking: (1-a)(1-b)(1-c) = total multiplier
   * @param {number} tierSavings - Tier savings percentage (0-100)
   * @param {number} planSavings - Selling plan savings percentage (0-100)
   * @param {Array} discountConfigs - Array of discount savings configs
   * @param {string} purchaseType - 'one_time' or 'subscription'
   * @param {number} quantity - Current total quantity
   * @returns {number} Combined savings percentage (0-100)
   */
  function calculateCombinedSavings(
    tierSavings,
    planSavings,
    discountConfigs,
    purchaseType,
    quantity,
  ) {
    let multiplier = 1;

    // Apply tier savings
    if (tierSavings > 0) {
      multiplier *= 1 - tierSavings / 100;
    }

    // Apply selling plan savings
    if (planSavings > 0) {
      multiplier *= 1 - planSavings / 100;
    }

    // Apply matching discount savings blocks
    if (discountConfigs && discountConfigs.length > 0) {
      discountConfigs.forEach((config) => {
        if (config.discount_percentage <= 0) return;
        if (quantity < config.min_quantity) return;

        const appliesTo = config.applies_to || "both";
        const matches = appliesTo === "both" || appliesTo === purchaseType;

        if (matches) {
          multiplier *= 1 - config.discount_percentage / 100;
        }
      });
    }

    return Math.round((1 - multiplier) * 100);
  }

  /**
   * Get CTA button text based on current state
   * @param {number} totalQty - Current total quantity
   * @param {number} effectiveMin - Effective minimum
   * @param {string} labelPlural - Plural label (e.g., "boxes")
   * @param {string} labelSingular - Singular label (e.g., "box")
   * @param {string} ctaValid - Valid state CTA text
   * @returns {string} CTA button text
   */
  function getCtaText(
    totalQty,
    effectiveMin,
    labelPlural,
    labelSingular,
    ctaValid,
  ) {
    const remaining = effectiveMin - totalQty;

    if (remaining <= 0) return ctaValid;
    // First selection should always say "select at least", even when min=1
    if (totalQty === 0) {
      const label = effectiveMin === 1 ? labelSingular : labelPlural;
      return `Select at least ${effectiveMin} ${label}`;
    }
    // Only show "select your last" if user has already selected something
    if (remaining === 1) return `Select your last ${labelSingular}`;
    return `Select ${remaining} more ${labelPlural}`;
  }

  /**
   * Filter selling plans based on visibility setting
   * @param {Array} allPlans - All available selling plans
   * @param {string} visibility - Visibility setting
   * @returns {Array} Filtered selling plans
   */
  function filterSellingPlans(allPlans, visibility) {
    if (visibility === "one_time_only") return [];
    return allPlans || [];
  }

  /**
   * Get default selling plan based on tier, visibility, and default purchase type
   * @param {string|null} tierPlanId - Tier's selling plan ID
   * @param {string} visibility - Visibility setting
   * @param {Array} allPlans - All available plans
   * @param {string} defaultPurchaseType - Default purchase type ('one_time' or 'subscription')
   * @returns {string|null} Default selling plan ID
   */
  function getDefaultSellingPlan(
    tierPlanId,
    visibility,
    allPlans,
    defaultPurchaseType,
  ) {
    // If one-time only visibility, always return null
    if (visibility === "one_time_only") return null;

    // If tier has a selling plan ID, use it
    if (tierPlanId) return String(tierPlanId);

    // If visibility is both, or if default is subscription, select first available plan
    if (
      (visibility === "both" || defaultPurchaseType === "subscription") &&
      allPlans &&
      allPlans.length > 0
    ) {
      return String(allPlans[0].id);
    }

    // Default to one-time (null)
    return null;
  }

  /**
   * Generate a unique bundle ID
   * @returns {string} Unique bundle ID
   */
  function generateBundleId() {
    return (
      "bundle_" +
      Date.now().toString(36) +
      "_" +
      Math.random().toString(36).substr(2, 9)
    );
  }

  /**
   * Normalize selling plan ID to a consistent format for comparison
   * Handles both "gid://shopify/SellingPlan/123456" and "123456" formats
   * @param {string|number} id - Selling plan ID in any format
   * @returns {string} Normalized ID (numeric string)
   */
  function normalizeSellingPlanId(id) {
    if (!id) return "";
    const idStr = String(id);
    // Extract numeric ID from GID format if present
    const match = idStr.match(/SellingPlan\/(\d+)/);
    return match ? match[1] : idStr;
  }

  /**
   * Find a selling plan by ID, handling different ID formats
   * @param {Array} sellingPlans - Available selling plans
   * @param {string} planId - Plan ID to find (any format)
   * @returns {Object|undefined} Found plan or undefined
   */
  function findSellingPlan(sellingPlans, planId) {
    if (!sellingPlans || !planId) return undefined;
    const normalizedId = normalizeSellingPlanId(planId);
    return sellingPlans.find(
      (p) => normalizeSellingPlanId(p.id) === normalizedId,
    );
  }

  /**
   * Calculate tier display price
   * @param {Object} tier - Tier configuration
   * @param {number} basePrice - Base price from product
   * @param {string|null} selectedPlanId - Currently selected selling plan ID
   * @param {Array} sellingPlans - Available selling plans
   * @returns {string} Formatted price string
   */
  function calculateTierPrice(tier, basePrice, selectedPlanId, sellingPlans) {
    // 1. Use manual display_price if set (but allow blank/whitespace to hide price)
    if (tier.display_price !== undefined && tier.display_price !== null) {
      const trimmed = String(tier.display_price).trim();
      if (trimmed === "") {
        return ""; // Return empty string to hide price
      }
      return trimmed;
    }

    // 2. Start with base price
    let price = basePrice;

    // 3. Apply tier savings percentage if set
    if (tier.savings_percent && tier.savings_percent > 0) {
      price = basePrice * (1 - tier.savings_percent / 100);
    }

    // 4. Apply selling plan discount if a plan is selected
    if (selectedPlanId && sellingPlans && sellingPlans.length > 0) {
      const selectedPlan = findSellingPlan(sellingPlans, selectedPlanId);
      if (
        selectedPlan &&
        selectedPlan.price_adjustments &&
        selectedPlan.price_adjustments.length > 0
      ) {
        const adjustment = selectedPlan.price_adjustments[0];
        if (adjustment.value_type === "percentage") {
          // Percentage discount (e.g., 10% off)
          price = price * (1 - adjustment.value / 100);
        } else if (adjustment.value_type === "fixed_amount") {
          // Fixed amount discount (value is in cents)
          price = price - adjustment.value / 100;
        } else if (adjustment.value_type === "price") {
          // Fixed price override (value is the new price in cents)
          price = adjustment.value / 100;
        }
      }
    }

    // Ensure price doesn't go negative
    price = Math.max(0, price);

    // Don't show price if base price is 0
    if (price === 0 || basePrice === 0) {
      return "";
    }

    // Format as currency
    return "$" + price.toFixed(2) + "/ea";
  }

  /**
   * Calculate tier bar progress percentage
   * @param {number} totalQty - Current total quantity
   * @param {Array} tiers - Sorted array of tiers
   * @returns {number} Progress percentage (0-100)
   */
  function calculateTierProgress(totalQty, tiers) {
    if (!tiers || tiers.length === 0) return 0;

    const sortedTiers = tiers
      .slice()
      .sort((a, b) => a.min_quantity - b.min_quantity);
    const maxTier = sortedTiers[sortedTiers.length - 1];

    if (totalQty >= maxTier.min_quantity) return 100;
    if (totalQty === 0) return 0;

    // Find current tier position
    let currentTierIndex = -1;
    for (let i = sortedTiers.length - 1; i >= 0; i--) {
      if (totalQty >= sortedTiers[i].min_quantity) {
        currentTierIndex = i;
        break;
      }
    }

    // Calculate progress
    const tierCount = sortedTiers.length;
    if (currentTierIndex === -1) {
      // Before first tier
      const firstTier = sortedTiers[0];
      const progressToFirst =
        (totalQty / firstTier.min_quantity) * (100 / tierCount);
      return Math.min(progressToFirst, (100 / tierCount) * 0.5);
    }

    // At or past a tier
    const baseProgress = ((currentTierIndex + 1) / tierCount) * 100;

    // Add partial progress toward next tier
    if (currentTierIndex < tierCount - 1) {
      const currentTier = sortedTiers[currentTierIndex];
      const nextTier = sortedTiers[currentTierIndex + 1];
      const progressInSegment =
        (totalQty - currentTier.min_quantity) /
        (nextTier.min_quantity - currentTier.min_quantity);
      const segmentWidth = 100 / tierCount;
      return baseProgress + progressInSegment * segmentWidth * 0.8; // 80% into next segment max
    }

    return baseProgress;
  }

  /**
   * Parse items from URL string
   * @param {string} itemsStr - Items string from URL (e.g., "123:2,456:1")
   * @returns {Object} Items map
   */
  function parseItemsFromUrl(itemsStr) {
    if (!itemsStr) return {};

    const items = {};
    itemsStr.split(",").forEach((pair) => {
      const [id, qty] = pair.split(":");
      if (id && qty) {
        items[id] = parseInt(qty, 10) || 0;
      }
    });
    return items;
  }

  /**
   * Serialize items to URL string
   * @param {Object} items - Items map
   * @returns {string} URL-safe items string
   */
  function serializeItemsToUrl(items) {
    return Object.entries(items)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => `${id}:${qty}`)
      .join(",");
  }

  // ========================================
  // URL State Management
  // ========================================

  const urlState = {
    get(key) {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    },

    set(key, value) {
      const params = new URLSearchParams(window.location.search);
      if (value === null || value === undefined || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      const newUrl = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
      window.history.replaceState({}, "", newUrl);
    },

    getAll() {
      const params = new URLSearchParams(window.location.search);
      return Object.fromEntries(params.entries());
    },
  };

  // ========================================
  // Alpine.js Component
  // ========================================

  function cBundle(sectionId) {
    // Return cached instance if already created (prevents duplicate state)
    if (componentInstances.has(sectionId)) {
      console.log("MM Bundle: Returning cached instance for", sectionId);
      return componentInstances.get(sectionId);
    }

    const instance = {
      // State
      bundleId: "",
      items: {},
      originalMin: null,
      selectedSellingPlan: null,
      modalItem: null,
      frequencyDropdownOpen: false,
      isSubmitting: false,
      _wasBelowSubscriptionMin: true, // Track if we were below the subscription minimum
      builderUxStep: 1,
      showStickyCta: false,
      _stickyCtaObserver: null,
      selectedPack: 3,
      config: {
        sectionId: "",
        mode: "variants",
        minQuantity: 1,
        maxQuantity: 10,
        quantityLabel: "items",
        quantityLabelSingular: "item",
        purchaseOptionsVisibility: "both",
        defaultPurchaseType: "subscription",
        ctaTextValid: "Proceed to Checkout",
        tierBarEnabled: false,
        tierBarCheckImage: "",
        tiers: [],
        freeGifts: [],
        items: [],
        sellingPlans: [],
        checkoutBehavior: "cart_drawer", // 'cart_page', 'cart_drawer', or 'checkout'
        reloadAfterAdd: false,
        cartDrawerDelay: 0,
        cartDrawerEvents: [
          "cart:update",
          "cart:open",
          "cart:updated",
          "cart:refresh",
        ],
        titleSplitDelimiter: "",
        titleSplitIndex: 0,
      },

      // Initialize
      init() {
        // Prevent duplicate initialization using global tracker
        if (initializedSections.has(sectionId)) {
          console.log(
            "MM Bundle: Already initialized for section",
            sectionId,
            "- skipping",
          );
          return;
        }

        // Also check DOM element for initialization marker
        const bundleEl = document.getElementById(`c-bundle-${sectionId}`);
        if (bundleEl && bundleEl.hasAttribute("data-c-initialized")) {
          console.log(
            "MM Bundle: DOM element already initialized for section",
            sectionId,
            "- skipping",
          );
          return;
        }

        // Mark as initialized
        initializedSections.add(sectionId);
        if (bundleEl) {
          bundleEl.setAttribute("data-c-initialized", "true");
        }

        console.log("MM Bundle: Initializing for section", sectionId);

        // Load config from hydration script
        const configScript = document.querySelector(
          `#c-bundle-${sectionId} [data-c-bundle-config]`,
        );
        console.log("MM Bundle: Config script found:", !!configScript);

        if (configScript) {
          try {
            const rawText = configScript.textContent;
            console.log("MM Bundle: Raw config length:", rawText.length);
            const configData = JSON.parse(rawText);
            console.log("MM Bundle: Parsed config:", configData);
            console.log(
              "MM Bundle: Items count:",
              configData.items?.length || 0,
            );
            Object.assign(this.config, configData);
          } catch (e) {
            console.error("MM Bundle: Failed to parse config", e);
            console.error(
              "MM Bundle: Config text:",
              configScript.textContent.substring(0, 500),
            );
          }
        } else {
          console.warn(
            "MM Bundle: No config script found for section",
            sectionId,
          );
        }

        // Load state from URL
        this.bundleId = urlState.get("bundle_id") || generateBundleId();
        this.items = parseItemsFromUrl(urlState.get("items"));
        // On page reload, only raise selectedPack if restored qty exceeds the default
        const restoredQty = calculateTotalQuantity(this.items);
        if (restoredQty > this.selectedPack) this.selectedPack = restoredQty;
        this.originalMin = parseInt(urlState.get("original_min"), 10) || null;
        const builderElement = document.querySelector(`#c-bundle-${sectionId}`);
        this.checkoutBehavior =
          builderElement?.getAttribute("data-checkout-behavior") ||
          "cart_drawer";
        this.reloadAfterAdd =
          builderElement?.getAttribute("data-reload-after-add") === "true";
        this.cartDrawerDelay = parseInt(
          builderElement?.getAttribute("data-cart-drawer-delay") || "0",
          10,
        );
        this.config.titleSplitDelimiter =
          builderElement?.getAttribute("data-title-split-delimiter") || "";
        this.config.titleSplitIndex = parseInt(
          builderElement?.getAttribute("data-title-split-index") || "0",
          10,
        );

        // Set default selling plan based on default tier (effectiveMin) and default purchase type
        const defaultTier = this.defaultTier;
        this.selectedSellingPlan = getDefaultSellingPlan(
          defaultTier?.selling_plan_id,
          this.config.purchaseOptionsVisibility,
          this.availableSellingPlans,
          this.config.defaultPurchaseType || "subscription",
        );

        // Sync initial state to URL
        this.syncToUrl();

        // Watch for changes and sync to URL
        this.$watch("items", () => this.syncToUrl());

        // Watch for quantity changes to auto-switch selling plan if enabled
        if (this.config.autoSwitchSellingPlan) {
          let previousTierMin = this.activeTier?.min_quantity;

          this.$watch("totalQuantity", (newQty) => {
            const newTier = getActiveTier(newQty, this.filteredTiers);
            const newTierMin = newTier?.min_quantity;

            // Only switch if we've moved to a different tier
            if (newTierMin !== previousTierMin && newTier?.selling_plan_id) {
              this.selectedSellingPlan = newTier.selling_plan_id;
            }
            previousTierMin = newTierMin;
          });
        }

        // Check if we should open cart drawer (after page reload)
        this.checkAndOpenCartDrawer();

        // Sticky CTA on mobile: show when scrolled past .c-bundle__cta-btn (if enabled)
        if (this.config.stickyCtaEnabled) {
          this.$nextTick(() => {
            this.setupStickyCtaObserver();
          });
          this.$watch("showStickyCta", (visible) => {
            document.body.classList.toggle(
              "c-bundle__sticky-cta-visible",
              !!visible,
            );
          });
          this.$watch("builderUxStep", (step) => {
            if (step !== 1) {
              this.showStickyCta = false;
              this.disconnectStickyCtaObserver();
            } else {
              this.setupStickyCtaObserver();
            }
          });
          window.addEventListener("resize", () => {
            if (window.innerWidth >= 768) this.showStickyCta = false;
          });
        }

        // Initialize Swipers
        this.InitSwipers();
      },

      setupStickyCtaObserver() {
        this.disconnectStickyCtaObserver();
        const sectionId = this.config.sectionId;
        if (!sectionId) return;
        const bundleEl = document.getElementById(`c-bundle-${sectionId}`);
        if (!bundleEl) return;
        const ctaBtn = bundleEl.querySelector(".c-bundle__cta-btn");
        if (!ctaBtn) return;
        this._stickyCtaObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const rect = entry.boundingClientRect;
              const isScrolledPast = !entry.isIntersecting && rect.top < 0;
              const isMobile =
                typeof window !== "undefined" && window.innerWidth < 768;
              this.showStickyCta = isMobile && isScrolledPast;
            }
          },
          { threshold: 0, rootMargin: "0px" },
        );
        this._stickyCtaObserver.observe(ctaBtn);
      },

      disconnectStickyCtaObserver() {
        if (this._stickyCtaObserver) {
          this._stickyCtaObserver.disconnect();
          this._stickyCtaObserver = null;
        }
      },

      scrollToUxStep() {
        const sectionId = this.config.sectionId;
        if (!sectionId) return;
        const bundleEl = document.getElementById(`c-bundle-${sectionId}`);
        if (!bundleEl) return;
        const target = bundleEl.querySelector(".c-bundle__ux_step");
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      },

      // Check for c_open_cart param and open cart drawer if present
      checkAndOpenCartDrawer() {
        const url = new URL(window.location.href);
        if (url.searchParams.get("c_open_cart") === "1") {
          console.log("MM Bundle: Opening cart drawer after page reload");

          // Remove the param from URL without reloading
          url.searchParams.delete("c_open_cart");
          window.history.replaceState({}, "", url.toString());

          // Wait a moment for page to fully render, then open cart drawer
          setTimeout(() => {
            document.dispatchEvent(
              new CustomEvent("cart:drawer:refresh", {
                bubbles: true,
                detail: { open: true },
              }),
            );
            const events = this.config.cartDrawerEvents || [
              "cart:update",
              "cart:open",
              "cart:updated",
              "cart:refresh",
            ];
            events.forEach((eventName) => {
              if (eventName && eventName.trim()) {
                document.dispatchEvent(
                  new CustomEvent(eventName.trim(), { bubbles: true }),
                );
                console.log("MM Bundle: Dispatched event:", eventName.trim());
              }
            });
          }, 500);
        }
      },

      // Sync state to URL
      syncToUrl() {
        urlState.set("bundle_id", this.bundleId);
        urlState.set("items", serializeItemsToUrl(this.items));
        if (this.originalMin) {
          urlState.set("original_min", this.originalMin);
        }
      },

      // Computed: Effective minimum
      get effectiveMin() {
        let configMin = this.config.minQuantity;
        if (this.builderUxStep === 1) {
          configMin = this.selectedPack;
        }
        return getEffectiveMin(this.originalMin, this.config.minQuantity);
      },

      // Computed: Total quantity
      get totalQuantity() {
        return calculateTotalQuantity(this.items);
      },

      // Computed: Remaining to minimum
      get remaining() {
        return Math.max(0, this.effectiveMin - this.totalQuantity);
      },

      // Computed: Is valid for checkout
      get isValid() {
        return validateQuantity(
          this.totalQuantity,
          this.effectiveMin,
          this.config.maxQuantity,
          this.selectedPack,
        );
      },

      // Helper: Check if a tier applies to the current purchase type
      tierAppliesToCurrentType(tier) {
        if (!tier.applies_to || tier.applies_to === "both") return true;
        return tier.applies_to === this.currentPurchaseType;
      },

      // Helper: Get tier type label (for purchase type display)
      getTierTypeLabel(tier) {
        const mode = this.config.tierMismatchDisplay;
        if (!mode || mode === "hide" || mode === "show_disabled") return "";

        // show_label_always: Always show label if tier has a specific applies_to
        if (mode === "show_label_always") {
          if (tier.applies_to === "subscription") return "Subscription only";
          if (tier.applies_to === "one_time") return "One-time only";
          return "";
        }

        // show_label_mismatch: Show label only when mismatched
        if (mode === "show_label_mismatch") {
          if (!this.tierAppliesToCurrentType(tier)) {
            if (tier.applies_to === "subscription") return "Subscription only";
            if (tier.applies_to === "one_time") return "One-time only";
          }
        }
        return "";
      },

      // Helper: Check if tier should show as disabled
      isTierDisabled(tier) {
        const mode = this.config.tierMismatchDisplay;
        // Disabled when mismatched AND in any show mode
        if (mode && mode.startsWith("show_")) {
          return !this.tierAppliesToCurrentType(tier);
        }
        return false;
      },

      // Helper: Get qualify badge text for mismatched tier
      getTierQualifyBadge(tier) {
        if (!this.isTierDisabled(tier)) return "";
        if (tier.applies_to === "subscription")
          return (
            this.config.qualifyBadgeSubscriptionText || "Subscribe to qualify"
          );
        if (tier.applies_to === "one_time")
          return this.config.qualifyBadgeOnetimeText || "One-time to qualify";
        return "";
      },

      // Computed: Filtered tiers based on current purchase type
      get filteredTiers() {
        const tiers = this.config.tiers || [];
        const mode = this.config.tierMismatchDisplay;
        // If any show mode, return all tiers
        if (mode && mode.startsWith("show_")) {
          return tiers;
        }
        // Otherwise filter to only matching tiers (hide mode)
        return tiers.filter((t) => this.tierAppliesToCurrentType(t));
      },

      // Computed: Active tier (based on total quantity for display)
      get activeTier() {
        // Use totalQuantity for determining which tier applies to current selection
        // This makes savings update dynamically as user adds items
        let totalQuantity = this.totalQuantity;
        if (this.builderUxStep === 1) {
          totalQuantity = this.selectedPack;
        }
        return getActiveTier(totalQuantity, this.filteredTiers);
      },

      // Computed: Default tier (based on effectiveMin for initial selling plan selection)
      get defaultTier() {
        return getActiveTier(this.effectiveMin, this.filteredTiers);
      },

      // Computed: Savings percentage (tier savings + selling plan discount + discount savings)
      get savingsPercent() {
        const tierSavings = this.activeTier?.savings_percent || 0;
        const planSavings = this.selectedSellingPlan
          ? this.getSellingPlanDiscount()
          : 0;
        const purchaseType = this.selectedSellingPlan
          ? "subscription"
          : "one_time";

        return calculateCombinedSavings(
          tierSavings,
          planSavings,
          this.config.discountSavings || [],
          purchaseType,
          this.totalQuantity,
        );
      },

      // Helper: Get selling plan discount percentage
      getSellingPlanDiscount() {
        if (!this.config.sellingPlans || this.config.sellingPlans.length === 0)
          return 0;
        const plan = findSellingPlan(
          this.config.sellingPlans,
          this.selectedSellingPlan,
        );
        if (plan?.price_adjustments?.[0]?.value_type === "percentage") {
          return plan.price_adjustments[0].value;
        }
        return 0;
      },

      // Computed: Sorted tiers for tier bar display (filtered by purchase type)
      get sortedTiers() {
        if (!this.filteredTiers || this.filteredTiers.length === 0) return [];
        return this.filteredTiers
          .slice()
          .sort((a, b) => a.min_quantity - b.min_quantity);
      },

      // Computed: Tier progress percentage for progress bar
      get tierProgressPercent() {
        return calculateTierProgress(this.totalQuantity, this.filteredTiers);
      },

      // Computed: CTA text
      get ctaText() {
        const target = Math.max(this.selectedPack, this.effectiveMin);
        return getCtaText(
          this.totalQuantity,
          target,
          "flavors",
          "flavor",
          this.config.ctaTextValid,
        );
      },

      // Computed: Current purchase type for gift filtering
      get currentPurchaseType() {
        return this.selectedSellingPlan ? "subscription" : "one_time";
      },

      // Helper: Check if a gift applies to the current purchase type
      giftAppliesToCurrentType(gift) {
        if (!gift.applies_to || gift.applies_to === "both") return true;
        return gift.applies_to === this.currentPurchaseType;
      },

      // Computed: Check if there's a mix of gift types (for showing labels)
      get hasMixedGiftTypes() {
        const gifts = this.config.freeGifts || [];
        const hasSubscriptionOnly = gifts.some(
          (g) => g.applies_to === "subscription",
        );
        const hasOneTimeOnly = gifts.some((g) => g.applies_to === "one_time");
        return hasSubscriptionOnly || hasOneTimeOnly;
      },

      // Computed: Filtered gifts based on current purchase type
      get filteredGifts() {
        const gifts = this.config.freeGifts || [];
        const mode = this.config.giftMismatchDisplay;
        // If any show mode, return all gifts
        if (mode && mode.startsWith("show_")) {
          return gifts;
        }
        // Otherwise filter to only matching gifts (hide mode)
        return gifts.filter((g) => this.giftAppliesToCurrentType(g));
      },

      // Computed: Unlocked gifts (filtered by purchase type)
      get unlockedGifts() {
        let quantity = this.totalQuantity;
        if (this.totalQuantity < this.selectedPack) {
          quantity = this.selectedPack;
        }
        return getUnlockedGifts(quantity, this.filteredGifts);
      },

      // Computed: Unlocked gifts count
      get unlockedGiftsCount() {
        return this.unlockedGifts.filter((g) => !g.always_included).length;
      },

      // Computed: Total gifts count (excluding always-included, filtered by purchase type)
      get totalGiftsCount() {
        return this.filteredGifts.filter((g) => !g.always_included).length;
      },

      // Computed: Quantity-based gifts (filtered by purchase type)
      get quantityBasedGifts() {
        return this.filteredGifts.filter((g) => !g.always_included);
      },

      // Computed: Always-included gifts (filtered by purchase type)
      get alwaysIncludedGifts() {
        return this.filteredGifts.filter((g) => g.always_included);
      },

      // Helper: Get gift type label (for purchase type display)
      getGiftTypeLabel(gift) {
        const mode = this.config.giftMismatchDisplay;
        if (!mode || mode === "hide" || mode === "show_disabled") return "";

        // show_label_always: Always show label if gift has a specific applies_to
        if (mode === "show_label_always") {
          if (gift.applies_to === "subscription") return "Subscription only";
          if (gift.applies_to === "one_time") return "One-time only";
          return "";
        }

        // show_label_mismatch: Show label only when mismatched
        if (mode === "show_label_mismatch") {
          if (!this.giftAppliesToCurrentType(gift)) {
            if (gift.applies_to === "subscription") return "Subscription only";
            if (gift.applies_to === "one_time") return "One-time only";
          }
        }
        return "";
      },

      // Helper: Check if gift should show as disabled
      isGiftDisabled(gift) {
        const mode = this.config.giftMismatchDisplay;
        // Disabled when mismatched AND in any show mode
        if (mode && mode.startsWith("show_")) {
          return !this.giftAppliesToCurrentType(gift);
        }
        return false;
      },

      // Helper: Get qualify badge text for mismatched gift
      getGiftQualifyBadge(gift) {
        if (!this.isGiftDisabled(gift)) return "";
        if (gift.applies_to === "subscription")
          return (
            this.config.qualifyBadgeSubscriptionText || "Subscribe to qualify"
          );
        if (gift.applies_to === "one_time")
          return this.config.qualifyBadgeOnetimeText || "One-time to qualify";
        return "";
      },

      // Computed: Show purchase options dropdown
      get showPurchaseOptions() {
        if (this.config.purchaseOptionsVisibility === "one_time_only")
          return false;
        return (
          this.availableSellingPlans.length > 0 ||
          this.config.purchaseOptionsVisibility === "both"
        );
      },

      // Computed: Show subscription option (based on quantity)
      get showSubscriptionOption() {
        // Check if subscription visibility is allowed by config
        if (this.config.purchaseOptionsVisibility === "one_time_only")
          return false;
        if (this.availableSellingPlans.length === 0) return false;

        // Check minimum quantity requirement
        const minQty = this.config.minQuantityForSubscription || 0;
        const isAboveMin = minQty === 0 || this.totalQuantity >= minQty;
        const isBelowMin = minQty > 0 && this.totalQuantity < minQty;

        if (isBelowMin) {
          // If subscription was selected but now hidden, switch to one-time
          if (this.selectedSellingPlan) {
            this.selectedSellingPlan = null;
          }
          // Track that we're below the threshold
          this._wasBelowSubscriptionMin = true;
          return false;
        }

        // Auto-switch TO subscription ONLY when crossing the threshold upward
        // (was below, now at/above) and default is subscription
        if (isAboveMin && this._wasBelowSubscriptionMin) {
          if (this.config.defaultPurchaseType === "subscription") {
            // Use setTimeout to avoid modifying state during render
            setTimeout(() => {
              // Only switch if still at/above min and no plan selected
              if (!this.selectedSellingPlan && this.totalQuantity >= minQty) {
                this.selectFirstPlan();
              }
            }, 0);
          }
          // Mark that we've crossed the threshold (no longer "below")
          this._wasBelowSubscriptionMin = false;
        }

        return true;
      },

      // Computed: Available selling plans (from product's selling_plan_groups)
      get availableSellingPlans() {
        return filterSellingPlans(
          this.config.sellingPlans || [],
          this.config.purchaseOptionsVisibility,
        );
      },

      // Computed: Selected selling plan name (for frequency selector)
      get selectedPlanName() {
        if (!this.selectedSellingPlan) return "";
        const plan = findSellingPlan(
          this.config.sellingPlans,
          this.selectedSellingPlan,
        );
        return plan ? plan.name : "";
      },

      // Computed: Subscription savings percentage (combined tier + plan + discount savings)
      get subscriptionSavingsPercent() {
        const planSavings = this.getSubscriptionBaseSavings();
        const tierSavings = this.activeTier?.savings_percent || 0;

        return calculateCombinedSavings(
          tierSavings,
          planSavings,
          this.config.discountSavings || [],
          "subscription",
          this.totalQuantity,
        );
      },

      // Helper: Get base subscription savings (from plan only, without stacking)
      getSubscriptionBaseSavings() {
        const plans = this.availableSellingPlans;
        if (!plans || plans.length === 0) return 0;

        // Get the selected plan, or fall back to first plan
        let targetPlan = null;
        if (this.selectedSellingPlan) {
          targetPlan = findSellingPlan(
            this.config.sellingPlans,
            this.selectedSellingPlan,
          );
        }
        if (!targetPlan) {
          targetPlan = plans[0];
        }

        if (
          targetPlan &&
          targetPlan.price_adjustments &&
          targetPlan.price_adjustments.length > 0
        ) {
          const adjustment = targetPlan.price_adjustments[0];
          if (adjustment.value_type === "percentage") {
            return adjustment.value;
          }
        }
        return 0;
      },

      // Computed: Original price (before any discount) - sum of item prices
      get ctaOriginalPrice() {
        let total = 0;
        if (this.builderUxStep === 1) {
          const variant = this.config.items[0];
          if (variant) return this.selectedPack * variant.price;
          return total; // In cents
        }
        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(
            (i) => String(i.id) === String(variantId),
          );
          if (item) total += item.price * qty;
        });
        return total; // In cents
      },

      // Computed: Discounted price for CTA (uses current savingsPercent which handles both purchase types)
      get ctaDiscountedPrice() {
        if (this.builderUxStep === 1) {
          const original = this.ctaOriginalPrice;
          const savingsPercent = this.savingsPercent;
          return Math.round(original * (1 - savingsPercent / 100));
        }
        const original = this.ctaOriginalPrice;
        const savingsPercent = this.savingsPercent;
        return Math.round(original * (1 - savingsPercent / 100));
      },

      // Computed: Subscription original price (before discount)
      get subscriptionOriginalPrice() {
        return this.ctaOriginalPrice; // Same calculation
      },

      // Computed: Subscription discounted price (after all savings)
      get subscriptionDiscountedPrice() {
        const original = this.subscriptionOriginalPrice;
        const savingsPercent = this.subscriptionSavingsPercent;
        return Math.round(original * (1 - savingsPercent / 100));
      },

      // Computed: Per-item price for subscription
      get subscriptionPerItemPrice() {
        // Check if any item has items_per_unit metafield
        let totalItems = 0;
        let hasMetafield = false;

        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(
            (i) => String(i.id) === String(variantId),
          );
          if (item && item.items_per_unit > 0) {
            hasMetafield = true;
            totalItems += item.items_per_unit * qty;
          }
        });

        // If no metafield, use total quantity
        if (!hasMetafield) {
          totalItems = this.totalQuantity;
        }

        if (totalItems === 0) return 0;
        return Math.round(this.subscriptionDiscountedPrice / totalItems);
      },

      // Computed: One-time original price (same as subscription - sum of item prices)
      get oneTimeOriginalPrice() {
        return this.ctaOriginalPrice;
      },

      // Computed: One-time discounted price (uses one-time savings - tier + discount blocks for one_time)
      get oneTimeDiscountedPrice() {
        const original = this.oneTimeOriginalPrice;
        const tierSavings = this.activeTier?.savings_percent || 0;

        // Calculate one-time savings (tier + discount blocks that apply to one_time)
        const oneTimeSavings = calculateCombinedSavings(
          tierSavings,
          0, // No selling plan discount for one-time
          this.config.discountSavings || [],
          "one_time",
          this.totalQuantity,
        );

        return Math.round(original * (1 - oneTimeSavings / 100));
      },

      // Computed: Per-item price for one-time
      get oneTimePerItemPrice() {
        // Check if any item has items_per_unit metafield
        let totalItems = 0;
        let hasMetafield = false;

        Object.entries(this.items).forEach(([variantId, qty]) => {
          const item = this.config.items.find(
            (i) => String(i.id) === String(variantId),
          );
          if (item && item.items_per_unit > 0) {
            hasMetafield = true;
            totalItems += item.items_per_unit * qty;
          }
        });

        // If no metafield, use total quantity
        if (!hasMetafield) {
          totalItems = this.totalQuantity;
        }

        if (totalItems === 0) return 0;
        return Math.round(this.oneTimeDiscountedPrice / totalItems);
      },

      // Computed: Check if all items in config have the same base price (variant mode only)
      get allVariantsSamePrice() {
        if (this.config.mode !== "variants") return false;
        if (!this.config.items || this.config.items.length < 2) return true;
        const firstPrice = this.config.items[0].price;
        return this.config.items.every((item) => item.price === firstPrice);
      },

      // Computed: Original single variant price (no discount)
      get singleItemOriginalPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        return this.config.items[0].price;
      },

      // Computed: Single variant price for one-time (base price with one-time discount applied)
      get oneTimeSingleItemPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        const basePrice = this.config.items[0].price;
        const tierSavings = this.activeTier?.savings_percent || 0;
        // For one-time, planSavings is 0 (no selling plan discount)
        const oneTimeSavings = calculateCombinedSavings(
          tierSavings,
          0,
          this.config.discountSavings,
          "one_time",
          this.totalQuantity,
        );
        return Math.round(basePrice * (1 - oneTimeSavings / 100));
      },

      // Computed: Single variant price for subscription (base price with subscription discount applied)
      get subscriptionSingleItemPrice() {
        if (!this.config.items || this.config.items.length === 0) return 0;
        const basePrice = this.config.items[0].price;
        return Math.round(
          basePrice * (1 - this.subscriptionSavingsPercent / 100),
        );
      },

      // Helper: Format money (cents to dollars)
      formatMoney(cents) {
        if (cents === 0 || cents === null || cents === undefined)
          return "$0.00";
        return "$" + (cents / 100).toFixed(2);
      },

      // Helper: Format title (split at delimiter if configured)
      formatTitle(title) {
        if (!title) return "";
        const delimiter = this.config.titleSplitDelimiter;
        if (!delimiter) return title;
        const parts = title.split(delimiter);
        const index = this.config.titleSplitIndex;
        if (index >= 0 && index < parts.length) {
          return parts[index].trim();
        }
        return title;
      },

      // Methods: Purchase options
      toggleFrequencyDropdown() {
        this.frequencyDropdownOpen = !this.frequencyDropdownOpen;
      },

      closeFrequencyDropdown() {
        this.frequencyDropdownOpen = false;
      },

      selectFirstPlan() {
        if (this.availableSellingPlans.length > 0) {
          this.selectedSellingPlan = String(this.availableSellingPlans[0].id);
        }
      },

      selectPlan(planId) {
        this.selectedSellingPlan = String(planId);
        this.frequencyDropdownOpen = false;
      },

      // Methods: Item management
      getItemQuantity(itemId) {
        return this.items[itemId] || 0;
      },

      // Debounce helper to prevent duplicate clicks from multiple Alpine event handlers
      _lastClickTime: 0,
      _isDebounced() {
        const now = Date.now();
        if (now - this._lastClickTime < 50) {
          return true; // Ignore clicks within 50ms of each other
        }
        this._lastClickTime = now;
        return false;
      },

      addItem(itemId) {
        if (this._isDebounced()) return;
        if (this.totalQuantity >= this.config.maxQuantity) return;

        this.items = {
          ...this.items,
          [itemId]: (this.items[itemId] || 0) + 1,
        };
      },

      incrementItem(itemId) {
        if (this._isDebounced()) return;
        if (this.totalQuantity >= this.config.maxQuantity) return;

        this.items = {
          ...this.items,
          [itemId]: (this.items[itemId] || 0) + 1,
        };
      },

      decrementItem(itemId) {
        if (this._isDebounced()) return;
        const currentQty = this.items[itemId] || 0;
        if (currentQty <= 0) return;

        const newItems = { ...this.items };
        if (currentQty === 1) {
          delete newItems[itemId];
        } else {
          newItems[itemId] = currentQty - 1;
        }
        this.items = newItems;
      },

      // Methods: Gift checking
      isGiftUnlocked(gift) {
        if (gift.always_included) return true;
        let quantity = this.totalQuantity;
        if (this.totalQuantity < this.selectedPack) {
          quantity = this.selectedPack;
        }
        return quantity >= gift.unlock_quantity;
      },

      // Methods: Get gift inline styles (for per-gift customization)
      getGiftStyles(gift) {
        const styles = [];
        if (gift.bg_color) {
          // Set CSS variable so it only applies to image wrapper, not entire card
          styles.push(`--c-gift-bg: ${gift.bg_color}`);
        }
        // Per-gift border settings
        if (gift.border_type && gift.border_type !== "none") {
          styles.push(`--c-gift-border-type: ${gift.border_type}`);
          styles.push(`--c-gift-border-width: ${gift.border_width || 1}px`);
          if (gift.border_color) {
            styles.push(`--c-gift-border-color: ${gift.border_color}`);
          }
        }
        if (gift.border_radius) {
          styles.push(`--c-gift-border-radius: ${gift.border_radius}px`);
        }
        return styles.join("; ");
      },

      // Methods: Tier bar
      getTierPosition(index) {
        const tierCount = this.sortedTiers.length;
        if (tierCount <= 1) return 50;
        // Evenly space tiers with padding on edges
        const padding = 10; // 10% padding on each side
        const usableWidth = 100 - padding * 2;
        return padding + (index / (tierCount - 1)) * usableWidth;
      },

      isTierActive(tier) {
        // Tier is active if we've reached it but haven't passed to the next higher tier
        const sorted = this.sortedTiers;
        const tierIndex = sorted.findIndex(
          (t) => t.min_quantity === tier.min_quantity,
        );
        const nextTier = sorted[tierIndex + 1];

        if (this.totalQuantity < tier.min_quantity) return false;
        if (!nextTier) return this.totalQuantity >= tier.min_quantity;
        return (
          this.totalQuantity >= tier.min_quantity &&
          this.totalQuantity < nextTier.min_quantity
        );
      },

      getTierDisplayPrice(tier) {
        return calculateTierPrice(
          tier,
          this.config.basePrice || 0,
          this.selectedSellingPlan,
          this.availableSellingPlans,
        );
      },

      getPriceByPack(packQuantity = 2) {
        const variant = this.config.items[0];
        // In cents
        return {
          original: packQuantity * variant.price,
          discounted: Math.round(
            packQuantity * variant.price * (1 - this.savingsPercent / 100),
          ),
        };
      },

      // Methods: Modal
      openInfoModal(item) {
        this.modalItem = item;
        this.$refs.infoModal?.showModal();
      },

      closeInfoModal() {
        this.$refs.infoModal?.close();
        this.modalItem = null;
      },

      openFlavorDialog() {
        this.builderUxStep = 2;
        document.body.style.overflow = "hidden";
        document.body.classList.add("c-bundle__flavor-dialog-open");
        this.$nextTick(() => {
          const sectionId = this.config.sectionId;
          const dialog =
            this.$refs?.flavorDialog ||
            (sectionId &&
              document.querySelector(
                `#c-bundle-${sectionId} .c-bundle__flavor-dialog`,
              ));
          if (dialog && typeof dialog.showModal === "function") {
            try {
              dialog.showModal();
            } catch (e) {
              console.warn("MM Bundle: Flavor dialog showModal failed", e);
              document.body.style.overflow = "";
              document.body.classList.remove("c-bundle__flavor-dialog-open");
            }
          }
        });
      },

      closeFlavorDialog() {
        const sectionId = this.config.sectionId;
        const dialog =
          this.$refs.flavorDialog ||
          (sectionId &&
            document.querySelector(
              `#c-bundle-${sectionId} .c-bundle__flavor-dialog`,
            ));
        if (dialog && typeof dialog.close === "function") {
          dialog.close();
        }
        document.body.style.overflow = "";
        document.body.classList.remove("c-bundle__flavor-dialog-open");
        this.builderUxStep = 1;
      },

      // Methods: Form submission
      async submitBundle() {
        if (!this.isValid || this.isSubmitting) return;

        this.isSubmitting = true;

        const formInputs = this.$refs.formInputs;
        if (!formInputs) {
          this.isSubmitting = false;
          return;
        }

        // Build items array for JSON submission (better for selling plans)
        const items = [];

        // Add selected items with selling plan (subscriptions only apply to bundle items, not gifts)
        Object.entries(this.items).forEach(([variantId, quantity]) => {
          if (quantity <= 0) return;

          const item = {
            id: parseInt(variantId, 10),
            quantity: quantity,
            properties: {
              _custom_bundle_id: this.bundleId,
              _bundle_item: "true",
            },
          };

          // Add selling plan to bundle items (not gifts)
          if (this.selectedSellingPlan) {
            item.selling_plan = parseInt(this.selectedSellingPlan, 10);
          }

          items.push(item);
        });

        // Get current cart to check for existing gifts
        let currentCart = null;
        try {
          const cartResponse = await fetch("/cart.js", {
            headers: { Accept: "application/json" },
          });
          currentCart = await cartResponse.json();
        } catch (e) {
          console.log("MM Bundle: Could not fetch cart, will add all gifts");
        }

        // Add unlocked free gifts (NO selling plan - gifts are one-time)
        this.unlockedGifts.forEach((gift) => {
          if (!gift.variant_id) return; // Skip virtual gifts

          // Check if gift should be skipped (already in cart and allow_multiple is false)
          if (!gift.allow_multiple && currentCart) {
            const giftInCart = currentCart.items.some(
              (item) => item.variant_id === gift.variant_id,
            );
            if (giftInCart) {
              console.log(
                "MM Bundle: Skipping gift (already in cart):",
                gift.title,
              );
              return;
            }
          }

          items.push({
            id: gift.variant_id,
            quantity: 1,
            properties: {
              _custom_bundle_id: this.bundleId,
              _bundle_gift: "true",
            },
            // No selling_plan for gifts - they're always one-time
          });
        });

        // Debug: Log the payload
        console.log(
          "MM Bundle: Submitting items:",
          JSON.stringify(items, null, 2),
        );
        console.log(
          "MM Bundle: Selected selling plan:",
          this.selectedSellingPlan,
        );

        // Handle checkout behavior
        if (this.checkoutBehavior === "cart_drawer") {
          // Cart Drawer: Add items via API, then open drawer
          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error("MM Bundle: Cart add error:", errorData);
              throw new Error(
                errorData.description || "Failed to add items to cart",
              );
            }

            const cartData = await response.json();
            console.log("MM Bundle: Items added to cart:", cartData);

            // If reload mode is enabled, reload the page with a param to open cart drawer
            if (this.reloadAfterAdd) {
              console.log("MM Bundle: Reloading page to open cart drawer");
              const url = new URL(window.location.href);
              url.searchParams.set("c_open_cart", "1");
              window.location.href = url.toString();
              return;
            }

            let freshCart;
            if (typeof window._cart?.get === "function") {
              freshCart = await window._cart.get();
            } else {
              const cartResponse = await fetch("/cart.js", {
                headers: { Accept: "application/json" },
              });
              freshCart = await cartResponse.json();
            }
            console.log("MM Bundle: Fresh cart data:", freshCart);

            // Try to refresh cart drawer section via Shopify's section rendering API
            try {
              const sectionResponse = await fetch(
                "/?sections=cart-drawer,cart-icon,cart-count",
              );
              if (sectionResponse.ok) {
                const sections = await sectionResponse.json();
                console.log("MM Bundle: Cart sections refreshed");

                Object.entries(sections).forEach(([sectionId, html]) => {
                  const sectionEl = document.getElementById(
                    `shopify-section-${sectionId}`,
                  );
                  if (sectionEl) {
                    sectionEl.innerHTML = html;
                    if (
                      typeof Alpine !== "undefined" &&
                      typeof Alpine.initTree === "function"
                    ) {
                      Alpine.initTree(sectionEl);
                    }
                  }
                });
              }
            } catch (sectionError) {
              console.log(
                "MM Bundle: Section refresh not available, using events",
              );
            }

            // Fire all configured cart drawer events with fresh cart data
            const events = this.config.cartDrawerEvents || [
              "cart:update",
              "cart:open",
              "cart:updated",
              "cart:refresh",
            ];
            const eventDetail = {
              bubbles: true,
              detail: {
                cart: freshCart,
                resource: freshCart,
                sourceId: this.config.sectionId,
                data: {
                  source: "c-bundle-builder",
                  itemCount: freshCart.item_count,
                },
              },
            };

            events.forEach((eventName) => {
              if (eventName && eventName.trim()) {
                const customEvent = new CustomEvent(
                  eventName.trim(),
                  eventDetail,
                );
                document.dispatchEvent(customEvent);
                console.log("MM Bundle: Dispatched event:", eventName.trim());
              }
            });

            if (this.cartDrawerDelay > 0) {
              await new Promise((resolve) =>
                setTimeout(resolve, this.cartDrawerDelay),
              );
            }

            if (
              typeof Alpine !== "undefined" &&
              typeof Alpine.nextTick === "function"
            ) {
              await new Promise((resolve) => Alpine.nextTick(resolve));
            }

            document.dispatchEvent(
              new CustomEvent("cart:drawer:open", { bubbles: true }),
            );
            // close the flavor dialog
            this.closeFlavorDialog();

            // Reset submitting state after cart drawer is triggered
            this.isSubmitting = false;
          } catch (error) {
            console.error("MM Bundle: Error adding to cart:", error);
            alert("Failed to add items to cart. Please try again.");
            this.isSubmitting = false;
          }
        } else if (this.checkoutBehavior === "checkout") {
          // Direct to Checkout: Add items via API, then redirect to /checkout
          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error("MM Bundle: Cart add error:", errorData);
              throw new Error(
                errorData.description || "Failed to add items to cart",
              );
            }

            console.log("MM Bundle: Items added, redirecting to checkout");
            window.location.href = "/checkout";
          } catch (error) {
            console.error("MM Bundle: Error adding to cart:", error);
            alert("Failed to add items to cart. Please try again.");
            this.isSubmitting = false;
          }
        } else {
          // Cart Page (default): Add items via API, then redirect to /cart
          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error("MM Bundle: Cart add error:", errorData);
              throw new Error(
                errorData.description || "Failed to add items to cart",
              );
            }

            console.log("MM Bundle: Items added, redirecting to cart");
            window.location.href = "/cart";
          } catch (error) {
            console.error("MM Bundle: Error adding to cart:", error);
            alert("Failed to add items to cart. Please try again.");
            this.isSubmitting = false;
          }
        }
      },

      InitSwipers() {
        // Skip if Swiper is not available
        if (typeof Swiper === "undefined") return;

        // Find swiper elements directly within the section
        let sectionContainer = this.$el;
        const mainSwiper = sectionContainer.querySelector("[data-c-hero-main]");
        const thumbsSwiper = sectionContainer.querySelector(
          "[data-c-hero-thumbs]",
        );
        const container = mainSwiper?.parentElement;

        if (!mainSwiper || !container) return;

        // Skip if already initialized
        if (mainSwiper.swiper) return;

        const paginationEl = container.querySelector(
          ".c-bundle__hero-pagination",
        );

        // Initialize thumbs swiper first if it exists
        let thumbsSwiperInstance = null;
        if (thumbsSwiper && !thumbsSwiper.swiper) {
          thumbsSwiperInstance = new Swiper(thumbsSwiper, {
            spaceBetween: 10,
            slidesPerView: "auto",
            freeMode: true,
            watchSlidesProgress: true,
            grabCursor: true,
          });
        }

        // Initialize main swiper
        const autoplayEnabled = mainSwiper.dataset.autoplay === "true";
        const autoplayDelay =
          parseInt(mainSwiper.dataset.autoplayDelay || "5", 10) * 1000;
        const showArrows = mainSwiper.dataset.showArrows === "true";
        const paginationType = mainSwiper.dataset.paginationType || "none";
        const scrollbarEl = container.querySelector(
          ".c-bundle__hero-scrollbar",
        );
        const progressBarEl = container.querySelector(
          ".c-bundle__hero-progress-bar",
        );

        const swiperConfig = {
          loop: false,
          grabCursor: true,
          mousewheel: {
            forceToAxis: true,
          },
          keyboard: {
            enabled: true,
          },
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : false,
          thumbs: thumbsSwiperInstance
            ? {
                swiper: thumbsSwiperInstance,
              }
            : undefined,
          autoplay: autoplayEnabled
            ? {
                delay: autoplayDelay,
                disableOnInteraction: true,
              }
            : false,
        };

        // Navigation arrows
        if (showArrows) {
          swiperConfig.navigation = {
            prevEl: mainSwiper.parentElement.querySelector(
              ".c-pdp-bundle__hero-arrow--prev",
            ),
            nextEl: mainSwiper.parentElement.querySelector(
              ".c-pdp-bundle__hero-arrow--next",
            ),
          };
        } else {
          swiperConfig.navigation = false;
        }

        // Add scrollbar if enabled
        if (paginationType === "scrollbar" && scrollbarEl) {
          swiperConfig.scrollbar = {
            el: scrollbarEl,
            draggable: true,
          };
        }

        const swiperInstance = new Swiper(mainSwiper, swiperConfig);

        // Handle progress bar animation
        if (paginationType === "progress" && progressBarEl && swiperInstance) {
          function updateProgressBar() {
            const progress = (swiperInstance.progress * 100).toFixed(2);
            progressBarEl.style.width = progress + "%";
          }

          // Update on slide change
          swiperInstance.on("slideChange", updateProgressBar);
          swiperInstance.on("progress", updateProgressBar);

          // Initial update
          updateProgressBar();
        }
      },

      e_ScrollToReviews() {
        document.dispatchEvent(new CustomEvent("open-written-reviews"));
      },
    };

    // Cache the instance
    componentInstances.set(sectionId, instance);
    console.log("MM Bundle: Created new instance for", sectionId);

    return instance;
  }

  function VideoReviews(sectionId) {
    return {
      activeTab: "video",
      modalVideoSrc: "",
      init() {
        document.addEventListener(
          "open-written-reviews",
          Alpine.debounce((e) => {
            console.log("MM Bundle: Opening written reviews", e);
            this.activeTab = "written";
            // Scroll to the reviews app block
            this.$nextTick(() => {
              const reviewsAppBlock =
                document.getElementById("reviews-app-block");
              if (reviewsAppBlock) {
                reviewsAppBlock.scrollIntoView({ behavior: "smooth" });
              }
            });
          }, 100),
        );
      },
    };
  }

  // ========================================
  // Export for testing
  // ========================================

  if (typeof window !== "undefined") {
    window.MMBundleLogic = {
      calculateTotalQuantity,
      getEffectiveMin,
      validateQuantity,
      getActiveTier,
      getUnlockedGifts,
      getCtaText,
      filterSellingPlans,
      getDefaultSellingPlan,
      generateBundleId,
      parseItemsFromUrl,
      serializeItemsToUrl,
    };
  }

  // ========================================
  // Register with Alpine.js
  // ========================================

  function registerComponent() {
    // Use global flag to prevent duplicate registration across script reloads
    if (window._cBundleState.registered) {
      console.log("MM Bundle: Component already registered globally, skipping");
      return;
    }
    if (typeof Alpine === "undefined" || !Alpine.data) {
      return;
    }

    try {
      Alpine.data("cBundle", cBundle);
      Alpine.data("VideoReviews", VideoReviews);
      window._cBundleState.registered = true;
      console.log("MM Bundle: Component registered successfully");
    } catch (e) {
      // If component already exists, that's okay - mark as registered
      if (e.message && e.message.includes("already registered")) {
        window._cBundleState.registered = true;
        console.log("MM Bundle: Component already registered");
      } else {
        console.error("MM Bundle: Failed to register component", e);
      }
    }
  }

  // ========================================
  // CRITICAL: Expose cBundle as global function
  // ========================================
  // This ensures Alpine can find it when evaluating x-data expressions
  // even if Alpine.data() registration has timing issues
  window.cBundle = cBundle;

  // Expose registration function globally
  window.cBundleRegister = registerComponent;

  // Register immediately if Alpine is already available
  if (typeof Alpine !== "undefined" && Alpine.data) {
    registerComponent();
  }

  // Listen for alpine:init event (fires before Alpine scans DOM)
  document.addEventListener(
    "alpine:init",
    () => {
      registerComponent();
    },
    { once: true },
  );

  // Fallback: wait for Alpine if it's not available yet
  if (typeof Alpine === "undefined") {
    let attempts = 0;
    const maxAttempts = 100; // 5 seconds max wait
    const checkAlpine = () => {
      if (typeof Alpine !== "undefined" && Alpine.data) {
        registerComponent();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkAlpine, 50);
      }
    };
    checkAlpine();
  }

  // Handle Shopify customizer section reload
  document.addEventListener("shopify:section:load", function (event) {
    // Check if this is our section
    const bundleEl = event.target.querySelector('[x-data^="cBundle"]');
    if (!bundleEl) return;

    console.log("MM Bundle: Section reloaded, reinitializing Alpine...");

    // Ensure component is registered
    registerComponent();

    // Initialize Alpine on the new DOM element
    if (typeof Alpine !== "undefined" && Alpine.initTree) {
      // Small delay to ensure DOM is fully ready
      setTimeout(() => {
        Alpine.initTree(bundleEl);
      }, 10);
    }
  });

  // ========================================
  // Handle Barba.js page transitions
  // ========================================
  // This event is dispatched by page-transitions.ts after scripts are re-executed
  function reinitializeBundleComponents() {
    // Debounce: Prevent multiple rapid calls from destroying freshly initialized components
    const now = Date.now();
    const lastRun = window._cBundleState.lastReinitTime || 0;
    if (now - lastRun < 200) {
      console.log("MM Bundle: Skipping reinitialization (debounced)");
      return;
    }
    window._cBundleState.lastReinitTime = now;

    // FIRST: Clear stale initialization markers from cached HTML
    // These persist in Barba's page cache even after state is cleared
    document
      .querySelectorAll('[x-data^="cBundle"][data-c-initialized]')
      .forEach((el) => {
        el.removeAttribute("data-c-initialized");
      });

    // Also clear the initialization tracking since we're reinitializing
    window._cBundleState.initializedSections.clear();
    window._cBundleState.componentInstances.clear();

    // Now find uninitialized bundle components
    const bundleEls = document.querySelectorAll('[x-data^="cBundle"]');

    if (bundleEls.length === 0) return;

    console.log(
      "MM Bundle: Reinitializing after Barba transition, found",
      bundleEls.length,
      "components",
    );

    // Ensure component is registered
    registerComponent();

    // Initialize Alpine on each bundle element
    bundleEls.forEach((bundleEl) => {
      if (typeof Alpine !== "undefined") {
        // CRITICAL: Destroy Alpine's internal state FIRST
        // Without this, Alpine reuses cached component and doesn't re-evaluate x-data
        if (Alpine.destroyTree) {
          Alpine.destroyTree(bundleEl);
        }
        // Now reinitialize with fresh state
        if (Alpine.initTree) {
          Alpine.initTree(bundleEl);
        }
      }
    });
  }

  // Listen for the custom event dispatched after Barba scripts are loaded
  document.addEventListener(
    "barba:scripts:loaded",
    reinitializeBundleComponents,
  );

  // Also listen for DOMContentLoaded (covers initial page load)
  document.addEventListener("DOMContentLoaded", function () {
    // Small delay to ensure scripts have executed
    setTimeout(reinitializeBundleComponents, 100);
  });

  // CRITICAL: Also run immediately when script loads
  // This handles Barba transitions where external scripts are deferred
  // and barba:scripts:loaded fires BEFORE this script has loaded
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    // Document already loaded, run immediately with small delay for Alpine
    setTimeout(reinitializeBundleComponents, 50);
  }
})();
